import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, CornerDownLeft, Loader2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";

import { useDebounced } from "../lib/debounce";
import { useDialogFocus } from "./ui/useDialogFocus";

export interface CommandItem {
  /** Identificador estable. Usado para keys. */
  id: string;
  /** Texto principal mostrado en la lista. */
  label: string;
  /** Línea secundaria (grupo / contexto / atajo). */
  description?: string;
  icon: LucideIcon;
  /** Para agrupar visualmente. "Socios" lo usan los resultados remotos. */
  category: "Pantallas" | "Acciones" | "Console" | "Socios";
  /** Palabras adicionales que matchean el filtro (sinónimos del oficio:
   *  "pago" → Caja, "deudores" → Morosidad). */
  keywords?: ReadonlyArray<string>;
  /** Lo que se ejecuta al confirmar. Cierre del palette es automático. */
  perform: () => void;
}

/** Búsqueda contra la API (ej. socios por nombre o RUT). Los resultados no
 *  se vuelven a filtrar en el cliente: ya vienen filtrados por el servidor. */
export interface FuenteRemota {
  /** Clave de cache de TanStack Query (se le agrega el texto buscado). */
  queryKey: string;
  /** Mínimo de caracteres para consultar. */
  minChars: number;
  buscar: (texto: string) => Promise<CommandItem[]>;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  items: ReadonlyArray<CommandItem>;
  fuenteRemota?: FuenteRemota | undefined;
}

const CATEGORY_ORDER: ReadonlyArray<CommandItem["category"]> = [
  "Pantallas",
  "Socios",
  "Acciones",
  "Console",
];

/** Minúsculas y sin tildes: "auditoria" encuentra "Auditoría". */
export function normalizarBusqueda(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/** Match local: cada palabra tiene que aparecer en el label, la descripción
 *  o alguna keyword (AND entre palabras). */
export function coincideItem(item: CommandItem, query: string): boolean {
  const tokens = normalizarBusqueda(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const campos = [item.label, item.description ?? "", ...(item.keywords ?? [])].map(
    normalizarBusqueda,
  );
  return tokens.every((t) => campos.some((c) => c.includes(t)));
}

export function CommandPalette({
  open,
  onClose,
  items,
  fuenteRemota,
}: CommandPaletteProps): JSX.Element | null {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useDialogFocus(open, panelRef, panelRef, inputRef);

  const textoRemoto = useDebounced(query.trim(), 250);
  const consultaRemota =
    open && fuenteRemota !== undefined && textoRemoto.length >= fuenteRemota.minChars;
  const remotoQ = useQuery({
    queryKey: [fuenteRemota?.queryKey ?? "palette-remota", textoRemoto],
    queryFn: () => (fuenteRemota ? fuenteRemota.buscar(textoRemoto) : Promise.resolve([])),
    enabled: consultaRemota,
    staleTime: 30 * 1000,
  });

  const filtered = useMemo<ReadonlyArray<CommandItem>>(() => {
    const locales = query.trim() ? items.filter((it) => coincideItem(it, query)) : items;
    const remotos = consultaRemota ? remotoQ.data ?? [] : [];
    // Orden plano = orden visual (por categoría), para que ↑↓ y Enter
    // recorran la lista en el mismo orden en que se ve.
    const todos = [...locales, ...remotos];
    return CATEGORY_ORDER.flatMap((cat) => todos.filter((it) => it.category === cat));
  }, [query, items, consultaRemota, remotoQ.data]);

  const grouped = useMemo(
    () =>
      CATEGORY_ORDER.flatMap((cat) => {
        const arr = filtered.filter((it) => it.category === cat);
        return arr.length > 0 ? [{ category: cat, items: arr }] : [];
      }),
    [filtered],
  );

  // Reset query y selección cada vez que se abre.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  // Reset selección al cambiar los resultados.
  useEffect(() => {
    setActiveIndex(0);
  }, [query, remotoQ.data]);

  // Navegación por teclado: ↑↓, Enter, Esc.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const item = filtered[activeIndex];
        if (item) {
          item.perform();
          onClose();
        }
        return;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, filtered, activeIndex, onClose]);

  // Mantener el item activo visible cuando se navega con teclado.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: "nearest", behavior: "auto" });
  }, [activeIndex, open]);

  if (!open) return null;

  const buscandoRemoto = consultaRemota && remotoQ.isFetching;
  const pistaSocios =
    fuenteRemota !== undefined &&
    query.trim().length > 0 &&
    query.trim().length < fuenteRemota.minChars;
  const activo = filtered[activeIndex];

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Buscar pantallas, socios y acciones"
      className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh]"
    >
      {/* Backdrop: la paleta no tiene datos que perder, un clic fuera cierra. */}
      <button
        type="button"
        aria-label="Cerrar búsqueda"
        className="absolute inset-0 bg-neutral-900/40 backdrop-blur-sm"
        onClick={onClose}
        tabIndex={-1}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="relative w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-neutral-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 border-b border-neutral-200 px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden="true" />
          <input
            ref={inputRef}
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              fuenteRemota
                ? "Buscar pantallas, socios (nombre o RUT) y acciones…"
                : "Buscar pantallas y acciones…"
            }
            className="flex-1 bg-transparent text-sm text-neutral-900 placeholder:text-neutral-500 focus:outline-none"
            aria-label="Buscar"
            role="combobox"
            aria-expanded="true"
            aria-controls="paleta-resultados"
            aria-activedescendant={activo ? `paleta-item-${activeIndex}` : undefined}
            aria-autocomplete="list"
          />
          {buscandoRemoto && (
            <Loader2
              className="h-4 w-4 shrink-0 animate-spin text-neutral-500"
              aria-label="Buscando socios"
            />
          )}
          <Kbd>esc</Kbd>
        </div>

        {/* Lista de items */}
        <div
          ref={listRef}
          id="paleta-resultados"
          role="listbox"
          aria-label="Resultados"
          className="max-h-[50vh] overflow-y-auto py-2"
        >
          {filtered.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-neutral-600" role="status">
              {buscandoRemoto ? (
                "Buscando…"
              ) : (
                <>
                  No encontramos resultados para{" "}
                  <span className="font-medium text-neutral-800">"{query}"</span>.
                </>
              )}
            </div>
          ) : (
            grouped.map((bucket) => (
              <CategoryBlock
                key={bucket.category}
                category={bucket.category}
                items={bucket.items}
                activeIndex={activeIndex}
                allItems={filtered}
                onSelect={(item) => {
                  item.perform();
                  onClose();
                }}
                onHover={(idx) => setActiveIndex(idx)}
              />
            ))
          )}
          {pistaSocios && (
            <p className="px-4 pb-2 pt-1 text-xs text-neutral-600">
              Escribe al menos {fuenteRemota.minChars} letras para buscar socios.
            </p>
          )}
          {remotoQ.isError && consultaRemota && (
            <p className="px-4 pb-2 pt-1 text-xs text-rose-700" role="alert">
              No se pudo buscar socios.
            </p>
          )}
        </div>

        {/* Footer con hints de teclado */}
        <div className="flex items-center justify-between border-t border-neutral-200 bg-neutral-50 px-4 py-2 text-xs text-neutral-600">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd>
              navegar
            </span>
            <span className="flex items-center gap-1">
              <Kbd>
                <CornerDownLeft className="h-3 w-3" aria-label="Enter" />
              </Kbd>
              abrir
            </span>
            <span className="flex items-center gap-1">
              <Kbd>esc</Kbd>
              cerrar
            </span>
          </div>
          <div className="hidden font-medium sm:block">InnovaClarke SSR</div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function CategoryBlock({
  category,
  items,
  activeIndex,
  allItems,
  onSelect,
  onHover,
}: {
  category: CommandItem["category"];
  items: ReadonlyArray<CommandItem>;
  activeIndex: number;
  allItems: ReadonlyArray<CommandItem>;
  onSelect: (item: CommandItem) => void;
  onHover: (globalIndex: number) => void;
}): JSX.Element {
  return (
    <div className="px-2 pb-1" role="group" aria-label={category}>
      <div
        className="px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-neutral-600"
        aria-hidden="true"
      >
        {category}
      </div>
      <div className="space-y-0.5">
        {items.map((item) => {
          const globalIdx = allItems.indexOf(item);
          const isActive = globalIdx === activeIndex;
          return (
            <button
              key={item.id}
              id={`paleta-item-${globalIdx}`}
              role="option"
              aria-selected={isActive}
              data-index={globalIdx}
              type="button"
              tabIndex={-1}
              onClick={() => onSelect(item)}
              onMouseMove={() => onHover(globalIdx)}
              className={twMerge(
                "group flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm transition-colors",
                isActive
                  ? "bg-brand-50 text-neutral-900"
                  : "text-neutral-700 hover:bg-neutral-50",
              )}
            >
              <item.icon
                className={twMerge(
                  "h-4 w-4 shrink-0",
                  isActive ? "text-brand-700" : "text-neutral-500",
                )}
                aria-hidden="true"
              />
              <span className="flex-1 truncate">
                <span className="font-medium">{item.label}</span>
                {item.description && (
                  <span className="ml-2 text-xs text-neutral-600">{item.description}</span>
                )}
              </span>
              {isActive && (
                <CornerDownLeft className="h-3.5 w-3.5 text-neutral-500" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <kbd className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-neutral-300 bg-white px-1 font-mono text-xs text-neutral-600">
      {children}
    </kbd>
  );
}

/**
 * Hook que registra el atajo global Cmd/Ctrl + K para abrir el palette.
 * Devuelve `[open, setOpen]` con el estado.
 */
export function useCommandPalette(): {
  open: boolean;
  setOpen: (value: boolean) => void;
} {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      // Cmd+K (Mac) o Ctrl+K (Windows/Linux). Ignoramos si hay otros
      // modificadores activos para no chocar con Ctrl+Shift+K, etc.
      const isComboKey = (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey;
      if (isComboKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return { open, setOpen };
}
